import React, { useState, useEffect } from "react";
import { FaRegEdit } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import "./styles/RequestForm.css";
import Done from "../assets/images/login-tick 1.png";
import { agentService } from '../services/AgentServices';

export default function RequestForm({ agent }) {
  const navigate = useNavigate();
  
  // Get agent ID from agent
  const agentId = agent?.id || agent?.agent_id;
  const agentRole = agent?.role || agent?.agent_role;
  
  // Determine initial status based on current role
  const isSenior = agentRole === 'SeniorBusinessAdsAgent';
  const isMini = agentRole === 'MiniBusinessAdsAgent';
  
  const [formData, setFormData] = useState({
    name: agent?.first_name || agent?.last_name ? 
          `${agent.first_name} ${agent.last_name}` : 
          agent?.name || agent?.agent_name || "",
    email: agent?.email || agent?.agent_email || "",
    currentRole: agentRole || "",
    action: "", // 'promote' or 'demote'
    notes: "",
    status: "pending", // For UI state
  });

  const [isUpdated, setIsUpdated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resultData, setResultData] = useState(null);

  // Update form when agent prop changes
  useEffect(() => {
    if (agent) {
      setFormData({
        name: agent?.first_name || agent?.last_name ? 
              `${agent.first_name} ${agent.last_name}` : 
              agent?.name || agent?.agent_name || "",
        email: agent?.email || agent?.agent_email || "",
        currentRole: agentRole || "",
        action: "",
        notes: "",
        status: "pending",
      });
    }
  }, [agent, agentRole]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Clear notes when changing action
    if (name === "action") {
      setFormData({
        ...formData,
        [name]: value,
        notes: "",
      });
      return;
    }
    
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!agentId) {
      setError("No agent ID found");
      return;
    }

    if (!formData.action) {
      setError("Please select an action (Promote or Demote)");
      return;
    }

    if (formData.action === 'demote' && !formData.notes.trim()) {
      setError("Please provide a reason for demotion");
      return;
    }

    // Check if action is valid based on current role
    if (formData.action === 'promote' && isSenior) {
      setError("Agent is already a Senior Business Ads Agent");
      return;
    }

    if (formData.action === 'demote' && isMini) {
      setError("Agent is already a Mini Business Ads Agent");
      return;
    }

    setLoading(true);
    setError("");

    try {
      let result;
      
      if (formData.action === 'promote') {
        // Call promote endpoint
        result = await agentService.promoteAgentToSenior(agentId, formData.notes);
        
        setResultData({
          type: 'promoted',
          newRole: 'SeniorBusinessAdsAgent',
          bonus: result.data?.promotion_bonus || 5000,
          ...result.data
        });
        
      } else if (formData.action === 'demote') {
        // Call demote endpoint
        result = await agentService.demoteAgentToMini(agentId, formData.notes);
        
        setResultData({
          type: 'demoted',
          newRole: 'MiniBusinessAdsAgent',
          reason: formData.notes,
          ...result.data
        });
      }

      setIsUpdated(true);
      
    } catch (err) {
      console.error("Error processing agent role change:", err);
      setError(err.response?.data?.message || err.message || "Failed to process request");
    } finally {
      setLoading(false);
    }
  };

  // Add these methods to your agentService if they don't exist
  // agentService.promoteAgentToSenior = async (agentId, notes = '') => {
  //   const response = await api.post(`/superadmin/agents/${agentId}/promote`, { notes });
  //   return response.data;
  // };

  // agentService.demoteAgentToMini = async (agentId, notes) => {
  //   const response = await api.post(`/superadmin/agents/${agentId}/demote`, { notes });
  //   return response.data;
  // };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleOkay = () => {
    navigate("/manage-agent"); // Changed to manage-agent instead of manage-business-agent
  };

  return (
    <div className="approval-form-container">
      <IoClose className="form-close" onClick={handleCancel} />
      
      {!isUpdated ? (
        <>
          <h2>Agent Role Management</h2>
          
          
          <form onSubmit={handleSubmit} className="approval-form">
            {/* Read-only agent info */}
            <label>Agent Name</label>
            <input value={formData.name} readOnly />

            <label>Email</label>
            <input value={formData.email} readOnly />

            <label>Current Role</label>
            <input 
              value={formData.currentRole === 'SeniorBusinessAdsAgent' ? 
                    'Senior Business Ads Agent' : 
                    formData.currentRole === 'MiniBusinessAdsAgent' ? 
                    'Mini Business Ads Agent' : 
                    'Unknown Role'} 
              readOnly 
              style={{
                color: formData.currentRole === 'SeniorBusinessAdsAgent' ? 'green' : 
                       formData.currentRole === 'MiniBusinessAdsAgent' ? 'blue' : 'gray',
                fontWeight: 'bold'
              }}
            />

            {/* Action selection */}
            <label>Select Action</label>
            <select
              name="action"
              value={formData.action}
              onChange={handleChange}
              disabled={loading}
              style={{color: "darkblue"}}
              required
            >
              <option value="">-- Select Action --</option>
              {!isSenior && (
                <option value="promote">Promote to Senior Business Ads Agent</option>
              )}
              {!isMini && (
                <option value="demote">Demote to Mini Business Ads Agent</option>
              )}
            </select>

            {/* Notes/Reason - only required for demotion */}
            {(formData.action === 'demote' || formData.notes) && (
              <>
                <label>
                  {formData.action === 'demote' ? "Reason for Demotion (Required)" : "Notes (Optional)"}
                </label>
                <div className="textarea-wrapper">
                  <textarea
                    name="notes"
                    placeholder={formData.action === 'demote' ? 
                               "Enter reason for demotion here..." : 
                               "Enter any additional notes here..."}
                    value={formData.notes}
                    onChange={handleChange}
                    disabled={loading}
                    required={formData.action === 'demote'}
                  />
                  <FaRegEdit className="textarea-icon" />
                </div>
                {formData.action === 'demote' && (
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    Reason is required for demotion
                  </p>
                )}
              </>
            )}




            {/* Error message */}
            {error && (
              <div style={{
                color: '#721c24', 
                background: '#f8d7da',
                padding: '10px',
                borderRadius: '4px',
                textAlign: 'center',
                margin: '10px 0'
              }}>
                {error}
              </div>
            )}

            {/* Action buttons */}
            <div className="form-actions">
              <button 
                type="submit" 
                className="btn-update" 
                disabled={loading || !formData.action}
                style={{
                  opacity: (!formData.action || (formData.action === 'demote' && !formData.notes.trim())) ? 0.6 : 1,
                  cursor: (!formData.action || (formData.action === 'demote' && !formData.notes.trim())) ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? "Processing..." : 
                 formData.action === 'promote' ? "Promote Agent" :
                 formData.action === 'demote' ? "Demote Agent" :
                 "Submit"}
              </button>
              
              <button 
                type="button" 
                className="btn-cancel" 
                onClick={handleCancel} 
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </form>
        </>
      ) : (
        <div className="approval-card">
          <img src={Done} alt="" className="done"/>
          <h2>
            {resultData?.type === 'promoted' ? 'Promotion Successful' : 
             resultData?.type === 'demoted' ? 'Demotion Successful' : 
             'Action Completed'}
          </h2>
          
          {resultData?.type === 'promoted' ? (
            <>
              <p style={{ marginTop: "-10px", marginBottom: "10px" }}>
                Agent has been promoted to Senior Business Ads Agent
              </p>
            </>
          ) : resultData?.type === 'demoted' ? (
            <>
              <p style={{ marginTop: "-10px", marginBottom: "10px" }}>
                Agent has been demoted to Mini Business Ads Agent
              </p>
            </>
          ) : null}
          
          <button className="btn-okay" onClick={handleOkay}>
            Done
          </button>
        </div>
      )}
    </div>
  );
}